import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin, ShieldCheck } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const SECTIONS = [
  {
    n: "01",
    title: "Our Commitment",
    body:
      "The Black House values the security of our website, applications and customer information. If you discover a potential security vulnerability affecting theblackhouse.in or services operated by The Black House, we encourage responsible disclosure so that we can investigate and resolve the issue promptly.",
  },
  {
    n: "02",
    title: "How To Report",
    body:
      "Please email a detailed report to support@theblackhouse.in. Include your name, contact information, affected URL, reproduction steps, screenshots or videos, proof of concept where appropriate, and your assessment of the potential impact.",
  },
  {
    n: "03",
    title: "Responsible Disclosure Guidelines",
    body:
      "Researchers must act in good faith, avoid disrupting services, avoid accessing or modifying customer data, respect privacy, keep findings confidential until resolved, comply with applicable laws, and refrain from social engineering, phishing, denial-of-service attacks, malware, spam, or attempts to gain financial benefit from vulnerabilities. We request reasonable time to investigate and remediate issues before public disclosure.",
  },
  {
    n: "04",
    title: "In Scope",
    body:
      "theblackhouse.in, official subdomains operated by The Black House, future official mobile applications, APIs and systems directly managed by The Black House.",
  },
  {
    n: "05",
    title: "Out Of Scope",
    body:
      "Third-party services not controlled by The Black House, vulnerabilities without demonstrable security impact, UI or spelling issues, automated scanner reports without proof of concept, social engineering, physical attacks, denial-of-service testing, brute-force attacks, public information disclosures, browser-specific legacy issues and vulnerabilities requiring compromised devices.",
  },
  {
    n: "06",
    title: "Recognition",
    body:
      "The Black House currently does not operate a monetary bug bounty programme. We may, at our discretion, acknowledge responsible researchers or provide a certificate of appreciation after successful verification and remediation.",
  },
  {
    n: "07",
    title: "Legal",
    body:
      "Activities outside these guidelines may result in refusal of recognition and may be subject to applicable legal remedies. This policy does not authorize testing that violates law or infringes the rights of others.",
  },
];

export default function Security() {
  useEffect(() => {
    document.title = "Responsible Disclosure — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="security-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Legal · Security
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Last Updated · 18 · VII · MMXXVI
      </motion.div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Responsible Disclosure
          </p>
          <h1
            data-testid="security-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            Guard The House
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-xl mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText("A quiet invitation to security researchers. If you find something, tell us first — and we will listen carefully, respectfully, and act without delay.")}
          </p>

          {/* Quick-report CTA */}
          <a
            href="mailto:support@theblackhouse.in?subject=Security%20Report%20%E2%80%94%20theblackhouse.in"
            data-testid="security-report-btn"
            className="group inline-flex items-center gap-3 mt-10 bg-white text-black font-display font-bold text-[11px] tracking-[0.3em] uppercase px-6 py-3 hover:bg-white/90 transition-colors duration-500"
          >
            <ShieldCheck size={14} strokeWidth={1.6} />
            Report A Vulnerability
          </a>
        </motion.div>
      </header>

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <div className="border-t border-[#141414]">
          {SECTIONS.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.9, delay: i * 0.03, ease }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-10 border-b border-[#141414]"
              data-testid={`security-section-${s.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
                  § {s.n}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[22px] leading-[1.2]"
                  style={{ letterSpacing: "0.16em" }}
                >
                  {s.title}
                </h2>
                <p className="font-serif-editorial text-[#c9c9c4] mt-4 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                  {cleanPolicyText(s.body)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="security-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft
              size={13}
              strokeWidth={1.4}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            Return To The House
          </Link>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/terms" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Terms</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/privacy" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Privacy</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/accessibility" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Accessibility</Link>
            <span className="text-[#2a2a2a]">·</span>
            <a href="https://www.instagram.com/theblackhouse.tbh" target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Instagram size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
            <a href="https://www.linkedin.com/company/the-black-house/" target="_blank" rel="noreferrer noopener" aria-label="LinkedIn" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Linkedin size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
